import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationItem, Notification } from './NotificationItem';
import { Settings } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface NotificationDropdownProps {
  onClose: () => void;
  onCountChange: (count: number) => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onClose,
  onCountChange
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { token } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/notifications?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
      onCountChange(data.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        onCountChange(0);
      }
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!token) return;

    // Mark as read if unread
    if (!notification.isRead) {
      try {
        await fetch(`${API_BASE_URL}/notifications/${notification.id}/read`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        setNotifications(prev => {
          const updated = prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n);
          // Calculate new unread count
          const newUnreadCount = updated.filter(n => !n.isRead).length;
          onCountChange(newUnreadCount);
          return updated;
        });
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }

    // Navigate to entity
    if (notification.entityType === 'Lead' && notification.entityId) {
      navigate(`/pipeline?lead=${notification.entityId}`);
    } else if (notification.entityType === 'Task' && notification.entityId) {
      navigate(`/tasks?task=${notification.entityId}`);
    }

    onClose();
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-semibold text-gray-900">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500">
            <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            Loading...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">{error}</div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-3xl mb-2">No notifications</div>
            <p className="text-sm">You're all caught up!</p>
          </div>
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClick={() => handleNotificationClick(notification)}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="p-2 border-t border-gray-200">
        <Link
          to="/settings#notifications"
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 p-2 rounded hover:bg-gray-50 transition-colors"
          onClick={onClose}
        >
          <Settings className="h-4 w-4" />
          Notification settings
        </Link>
      </div>
    </div>
  );
};
